import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { AppSchema } from "@cashsync/powersync";

/**
 * Legacy single-account DB filename. Kept as the fallback when no userId is
 * known (e.g. very first boot before priming) and so existing installs that
 * already have a `app.db` keep resolving to it for the guest/visitor.
 */
const DB_FILENAME = "app.db";

/**
 * Per-user PowerSync database filename. Multi-account stores each account's
 * data in its OWN local database so switching accounts never mixes rows. The
 * guest/visitor keeps the legacy `app.db` name for backward compatibility
 * (existing local data stays addressable); real accounts get a stable,
 * filesystem-safe per-userId name.
 */
export function dbFilenameForUser(userId: string | null | undefined): string {
  if (!userId || userId === "visitor") {
    return DB_FILENAME;
  }
  // Sanitize to a safe filename (OPFS / IndexedDB / SQLite path token).
  const safe = userId.replaceAll(/[^A-Za-z0-9_-]/g, "_");
  return `app-${safe}.db`;
}

const deleteDatabase = async (name: string): Promise<void> =>
  new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve();
      return;
    }
    const request = indexedDB.deleteDatabase(name);
    request.addEventListener("success", () => {
      resolve();
    });
    request.addEventListener("error", () => {
      resolve();
    });
    request.addEventListener("blocked", () => {
      resolve();
    });
  });

/**
 * Completely removes the local database, both from IndexedDB and the Origin
 * Private File System (OPFS). Useful to reset local state when corruption or
 * blocking schema changes occur.
 */
export async function clearLocalDatabase(userId?: string | null): Promise<void> {
  console.log("[PowerSync] Iniciando limpieza total de base de datos local...");

  // The per-account DB file (falls back to the legacy `app.db` for the
  // guest/visitor or when no userId is known).
  const dbName = dbFilenameForUser(userId);

  // 1. Clear IndexedDB
  if (typeof indexedDB !== "undefined") {
    const knownDatabaseNames = [
      dbName,
      `${dbName}-lock`,
      `idb://${dbName}`,
      "powersync",
      "powersync-sync",
    ];

    try {
      await Promise.all(knownDatabaseNames.map(deleteDatabase));
      console.log("[PowerSync] IndexedDB limpiada.");
    } catch (error) {
      console.error("[PowerSync] Error limpiando IndexedDB:", error);
    }
  }

  // 2. Clear OPFS (Origin Private File System)
  // This is where PowerSync stores the actual .db files in modern browsers.
  // The browser's "Clear site data" sometimes does NOT delete these.
  const storage = navigator.storage as Partial<StorageManager> | undefined;
  if (storage?.getDirectory) {
    try {
      const root = await navigator.storage.getDirectory();

      type KeysResult = FileSystemDirectoryHandle extends { keys(): infer R }
        ? R
        : AsyncIterableIterator<string>;

      interface DirectoryHandleWithKeys extends FileSystemDirectoryHandle {
        keys(): KeysResult;
      }

      const directory = root as DirectoryHandleWithKeys;
      for await (const name of directory.keys()) {
        await root.removeEntry(name, { recursive: true });
      }
      console.log("[PowerSync] OPFS (File System) limpiado.");
    } catch (error) {
      console.error("[PowerSync] Error limpiando OPFS:", error);
    }
  }
}

export function isIndexedDBError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message.includes("indexeddb") ||
    message.includes("backing store") ||
    message.includes("internal error opening") ||
    message.includes("unknownerror")
  );
}

declare global {
  interface Window {
    __TAURI__?: unknown;
    __TAURI_INTERNALS__?: unknown;
    process?: { type?: string };
  }
  var __TAURI_OS__: string | undefined;
}

function isTauri(): boolean {
  // oxlint-disable-next-line unicorn/prefer-global-this
  if (typeof window === "undefined") {
    return false;
  }
  // oxlint-disable-next-line unicorn/prefer-global-this
  return window.__TAURI__ !== undefined || window.__TAURI_INTERNALS__ !== undefined;
}

function isElectron(): boolean {
  if (typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("electron")) {
    return true;
  }
  // oxlint-disable-next-line unicorn/prefer-global-this, typescript/no-unnecessary-condition
  if (typeof window !== "undefined" && window.process?.type === "renderer") {
    return true;
  }
  return false;
}

type DesktopEnvironment = "tauri" | "electron" | "web";

/** @internal exported for tests*/
export function detectEnvironment(): DesktopEnvironment {
  if (isTauri()) {
    return "tauri";
  }
  if (isElectron()) {
    return "electron";
  }
  return "web";
}

/** @internal exported for tests*/
export function detectOS(): "windows" | "macos" | "linux" | "unknown" {
  const tauriOS = typeof __TAURI_OS__ === "string" ? __TAURI_OS__ : undefined;
  if (tauriOS) {
    if (tauriOS.includes("Windows")) {
      return "windows";
    }
    if (tauriOS.includes("Darwin") || tauriOS.includes("Mac")) {
      return "macos";
    }
    if (tauriOS.includes("Linux")) {
      return "linux";
    }
  }

  if (typeof navigator !== "undefined" && navigator.platform.length > 0) {
    const platform = navigator.platform.toLowerCase();
    if (platform.includes("win")) {
      return "windows";
    }
    if (platform.includes("mac")) {
      return "macos";
    }
    if (platform.includes("linux")) {
      return "linux";
    }
  }

  return "unknown";
}

async function createWebDatabase(
  isDev: boolean,
  userId?: string | null,
): Promise<AbstractPowerSyncDatabase> {
  const environment = detectEnvironment();
  const os = detectOS();
  const dbFilename = dbFilenameForUser(userId);
  console.log(
    `[PowerSync] Inicializando base de datos (env: ${environment}, OS: ${os}, db: ${dbFilename})`,
  );

  try {
    const { PowerSyncDatabase, WASQLiteOpenFactory, WASQLiteVFS } = await import("@powersync/web");

    // Always resolve PowerSync workers from the host root.
    const workerBasePath = "/@powersync/worker";
    const cacheBuster = isDev ? `?v=${Date.now()}` : "";

    // WebKitGTK on Linux has known OPFS issues — fall back to IndexedDB.
    const shouldUseIndexedDB = environment === "tauri" && os === "linux";

    const factoryOptions: ConstructorParameters<typeof WASQLiteOpenFactory>[0] = {
      dbFilename,
      worker: `${workerBasePath}/WASQLiteDB.umd.js${cacheBuster}`,
      ...(shouldUseIndexedDB && { vfs: WASQLiteVFS.IDBBatchAtomicVFS }),
    };

    console.log("[PowerSync] Configuring factory with worker:", factoryOptions.worker);
    const factory = new WASQLiteOpenFactory(factoryOptions);

    const db = new PowerSyncDatabase({
      schema: AppSchema,
      database: factory,
      flags: {
        enableMultiTabs: !isDev,
      },
      sync: {
        worker: `${workerBasePath}/SharedSyncImplementation.umd.js${cacheBuster}`,
      },
    });

    console.log("[PowerSync] Instancia de base de datos creada exitosamente.");
    return db;
  } catch (error) {
    console.error("[PowerSync] FATAL: Error creating the web database:", error);
    if (isIndexedDBError(error)) {
      console.warn("[PowerSync] Persistent storage error detected. Suggesting cleanup...");
    }
    throw error;
  }
}

// ─── Database Factory ──────────────────────────────────────────────

/**
 * Contract for platform-specific PowerSync database creation.
 * Each app (web, native) provides its own factory implementation.
 */
export interface DatabaseFactory {
  create(options: { isDev: boolean; userId?: string | null }): Promise<AbstractPowerSyncDatabase>;
}

/**
 * Pre-built web factory that uses WASM + OPFS via `@powersync/web`.
 * Can be used directly or wrapped by each app. The optional `userId` selects a
 * per-account database file (see {@link dbFilenameForUser}).
 */
export const webDatabaseFactory: DatabaseFactory = {
  create: async ({ isDev, userId }) => createWebDatabase(isDev, userId),
};

// ─── Query Logging ─────────────────────────────────────────────────
function wrapWithQueryLogging(db: AbstractPowerSyncDatabase): void {
  const methods = ["execute", "getAll", "get", "getOptional"] as const;

  for (const method of methods) {
    const original = Reflect.get(db, method);
    if (typeof original === "function") {
      Reflect.set(db, method, async (sql: string, params?: unknown[]) => {
        // Skip logging during initial sync as it floods the console and slows down the main thread.
        const status = db.currentStatus;
        const isTauriEnv = detectEnvironment() === "tauri";
        if (status.hasSynced || isTauriEnv) {
          console.log(`[SQL] ${method}:`, sql, params ?? []);
        }
        return Reflect.apply(original, db, [sql, params]);
      });
    }
  }
}

/**
 * Creates a PowerSync database using the provided factory.
 * Defaults to the web factory if none is provided (backward compatible).
 */
function isDatabaseFactory(obj: unknown): obj is DatabaseFactory {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }
  return "create" in obj && typeof (obj as Record<string, unknown>).create === "function";
}

/**
 * Creates a PowerSync database using the provided factory.
 * Defaults to the web factory if none is provided (backward compatible).
 */
export async function createPowerSyncDatabase(
  factoryOrOptions?: DatabaseFactory | { isDev?: boolean; userId?: string | null },
  maybeOptions?: { isDev?: boolean; userId?: string | null },
): Promise<AbstractPowerSyncDatabase> {
  const isFactory = isDatabaseFactory(factoryOrOptions);
  const factory = isFactory ? factoryOrOptions : webDatabaseFactory;
  const options = isFactory
    ? maybeOptions
    : (factoryOrOptions as { isDev?: boolean; userId?: string | null } | undefined);

  const globalDev = (globalThis as Record<string, unknown>).__DEV__;
  const isDev = options?.isDev ?? (typeof globalDev === "boolean" ? globalDev : false);
  const db = await factory.create({ isDev, userId: options?.userId });

  if (isDev) {
    wrapWithQueryLogging(db);
  }

  return db;
}

/**
 * Maps the result of getHasLocalDataQuery to a boolean.
 */
export function mapHasLocalData(rows: readonly { hasData: number }[] | undefined): boolean {
  return rows?.[0]?.hasData === 1;
}
/**
 * Helper to determine if the app is performing its initial data ingestion.
 */
export function isInitialSyncInitializing(status: {
  hasSynced: boolean;
  isConnected: boolean;
  isConnecting: boolean;
}): boolean {
  return !status.hasSynced && (status.isConnected || status.isConnecting);
}

/**
 * Helper to determine if the app is currently syncing (any phase).
 */
export function isSyncInProgress(status: { isConnected: boolean; isConnecting: boolean }): boolean {
  return status.isConnected || status.isConnecting;
}
